import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ApiError, trialErrorFrom } from '@grove/api-client';
import { api } from './api';

export type SaveFromLinkStatus = 'idle' | 'saving' | 'saved' | 'duplicate' | 'trial_expired' | 'error';

/**
 * Handles `/queue?save=<url>` — the "Save to Grove" button on a public share
 * page. Signed-out visitors are bounced through login by RequireAuth, which
 * preserves the param, so the save lands as soon as they have an account.
 *
 * The param is stripped once consumed: a reload shouldn't re-clip, and the
 * URL shouldn't keep a stale save request around.
 */
export function useSaveFromLink(): { status: SaveFromLinkStatus; dismiss: () => void } {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SaveFromLinkStatus>('idle');
  const started = useRef(false);

  const url = searchParams.get('save');

  useEffect(() => {
    if (!url || started.current) return;
    started.current = true;
    setStatus('saving');

    api
      .clipArticle({ url })
      .then(() => setStatus('saved'))
      .catch((err) => {
        // 409 means it's already in their queue — a success from the user's
        // point of view, worth saying out loud rather than flashing an error.
        if (err instanceof ApiError && err.status === 409) return setStatus('duplicate');
        if (trialErrorFrom(err)) return setStatus('trial_expired');
        setStatus('error');
      })
      .finally(() => {
        queryClient.invalidateQueries({ queryKey: ['articles'] });
        const params = new URLSearchParams(searchParams);
        params.delete('save');
        setSearchParams(params, { replace: true });
      });
  }, [url, searchParams, setSearchParams, queryClient]);

  return { status, dismiss: () => setStatus('idle') };
}
